import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from "@angular/material/snack-bar";
import { DateTimeUtils } from "src/app/shared/util/date-time.utils";
import { MixDesignService } from "src/app/shared/services/mix-design.service";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationDialogComponent } from "src/app/shared/components/confirmation-dialog/confirmation-dialog.component";

@Component({
    selector: 'app-mix-design-dashboard',
    templateUrl: './mix-design-dashboard.component.html',
    styleUrls: ['./mix-design-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MixDesignDashboardComponent implements OnInit {
  dataSource = new MatTableDataSource<any>();
  searchValue: string = '';
  displayedColumns: string[] = [
    'mixReferenceId',
    'mixId',
    'materialType',
    'designedByGeotherm',
    'date',
    'companyName',
    'projectName',
    'city',
    'state',
    'strength',
    'slump',
    'actions'
  ];

  constructor(
    private router: Router,
    public snackBar: MatSnackBar,
    public mixDesignService: MixDesignService,
    public dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadMixDesignList();
    this.dataSource.filterPredicate = (data: any, filter: string) => {

      const normalize = (value: string) => {
        return (value || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, '')
          .toLowerCase();
      };

      const normalizedFilter = normalize(filter);

      const mixIdMatch = normalize(data.mixId).includes(normalizedFilter);
      const searchStr = (
        data.materialType + ' ' +
        (data.designedByGeotherm ? 'geotherm' : 'client') + ' ' +
        data.companyName + ' ' +
        data.projectName + ' ' +
        data.city + ' ' +
        data.state + ' ' +
        data.country + ' ' +
        data.mixReferenceId
      ).toLowerCase();

      const globalMatch = searchStr.includes(filter.toLowerCase());
      return mixIdMatch || globalMatch;
    };
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim();

    this.searchValue = filterValue;
    this.dataSource.filter = filterValue.toLowerCase();
  }

  clearSearch() {
    this.searchValue = '';
    this.dataSource.filter = '';
  }

  createClientMixDesign() {
    this.router.navigateByUrl('mix-design/client/new');
  }

  createGeothermMixDesign() {
    this.router.navigateByUrl('mix-design/geotherm/new');
  }

  reDirectUrl() {
    this.router.navigate(['dashboard']);
  }

  onMixDesignClick(mixDesign: any): void {
    const params = new URLSearchParams({
      mixDesignCode: mixDesign.mixId,
      mixReference: mixDesign.mixReferenceId
    });

    window.open(`/mix-design/search?${params.toString()}`, '_blank');
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '';
    return DateTimeUtils.getLocalDateTimeFromUTCDate(dateString, 'MMMM d, yyyy HH:mm').toString();
  }

  loadMixDesignList() {
    this.mixDesignService.getAllMixDesigns().subscribe({
      next: (mixDesigns: any[]) => {
        this.dataSource.data = Array.isArray(mixDesigns) ? mixDesigns : [mixDesigns];
      }
    });
  }

  navigate(mixDesignId: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/mix-design/geotherm/edit/${mixDesignId}`])
    );
    window.open(url, '_blank');
  }

  editMixDesign(mixDesign: any) {
    if(mixDesign.designedByGeotherm) {
      this.router.navigate(['mix-design/geotherm/edit', mixDesign.id], {
        state: { mixDesign }
      });
    } else {
      this.router.navigate(['mix-design/client/edit', mixDesign.id], {
        state: { mixDesign }
      });
    }
  }

  copyRow(mixDesign: any) {
    if(mixDesign.designedByGeotherm) {
      this.router.navigate(['mix-design/geotherm/copy', mixDesign.id], {
        state: { mixDesign }
      });
    } else {
      this.router.navigate(['mix-design/client/copy', mixDesign.id], {
        state: { mixDesign }
      });
    }
  }

  removeRow(mixDesign: any) {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '450px',
      data: {
        title: 'Delete Mix Design',
        content: 'Are you sure you want to delete this Mix Design?',
        cancelText: 'NO',
        continueText: 'YES',
      },
      disableClose: true,
    }).afterClosed().subscribe((confirmed: string) => {
      if (confirmed !== 'YES') return;
      if (!mixDesign?.id) return;

      this.mixDesignService.deleteMixDesign(mixDesign.id).subscribe({
        next: () => {
          this.snackBar.open('Mix Design deleted successfully!', 'DONE', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
          });
          this.loadMixDesignList();
        },
        error: () => {
          this.snackBar.open('Failed to delete Mix Design', 'CLOSE', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
          });
        }
      });
    });
  }

  getFullCompanyLine(customer: any): string {
    const parts = [];

    if (customer.company?.name) parts.push(customer.company.name);
    if (customer.companyAddress?.city?.name) parts.push(customer.companyAddress.city.name);
    if (customer.companyAddress?.state?.name) parts.push(customer.companyAddress.state.name);
    if (customer.companyAddress?.country?.name) parts.push(customer.companyAddress.country.name);

    return parts.join(', ');
  }
}
